import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type { LoginSocialBarSdkData } from '@wix/thunderbolt-components';
import { isExperimentOpen } from '@wix/editor-elements-common-utils';

import type {
  ILoginSocialBarMapperProps,
  LoginSocialBarDefinition,
  LoginSocialBarCSSVars,
} from '../LoginSocialBar.types';
import {
  LoggedInTextState,
  AvatarIcon,
  Layout,
  ArrowShape,
} from '../LoginSocialBar.types';
import {
  CHOOSE_OPTION_TEXT_DEFAULT,
  LOG_OUT_TEXT,
  LOG_OUT_TEXT_DEFAULT,
  LOGGED_OUT_TEXT,
  LOGGED_OUT_TEXT_DEFAULT,
  LOGIN_SOCIAL_BAR_NAMESPACE,
  OPTION_VALUE_TEXT,
} from '../constants';

export const props = withCompInfo<
  ILoginSocialBarMapperProps,
  LoginSocialBarDefinition,
  ILoginSocialBarMapperProps
>()(
  [
    'compData',
    'compProps',
    'fullNameCompType',
    'language',
    'compId',
    'translate',
    'trackClicksAnalytics',
    'experiments',
  ],
  (
    { compData, compProps, translate, experiments, ...compPropsRest },
    carmiData,
  ) => {
    const enableLoggingInStateInSSR = isExperimentOpen(
      experiments,
      'specs.thunderbolt.LoginBarEnableLoggingInStateInSSR',
    );

    const splitAndDeprecateMemberInitials =
      compData.loggedInMember === Layout.MemberInitials;

    const loggedInDisplayMode = splitAndDeprecateMemberInitials
      ? Layout.AvatarOnly
      : compData.loggedInMember ?? Layout.AvatarAndText;

    const fallbackLoggedOutDisplayMode =
      compData.loggedInMember === Layout.TextOnly
        ? Layout.TextOnly
        : Layout.AvatarAndText;

    const loggedOutDisplayMode = compData.loggedOutMember
      ? compData.loggedOutMember
      : fallbackLoggedOutDisplayMode;

    const loggedInAvatarIcon = splitAndDeprecateMemberInitials
      ? AvatarIcon.Initials
      : compData.loggedInAvatarIcon ?? AvatarIcon.ProfileImage;
    const loggedOutAvatarIcon = compData.loggedOutAvatarIcon
      ? compData.loggedOutAvatarIcon
      : loggedInAvatarIcon;

    const loggedOutText =
      carmiData.loggedOutText === LOGGED_OUT_TEXT_DEFAULT
        ? translate(LOGIN_SOCIAL_BAR_NAMESPACE, LOGGED_OUT_TEXT) ||
          LOGGED_OUT_TEXT_DEFAULT
        : carmiData.loggedOutText;
    const logOutText =
      carmiData.logOutText === LOG_OUT_TEXT_DEFAULT
        ? translate(LOGIN_SOCIAL_BAR_NAMESPACE, LOG_OUT_TEXT) ||
          LOG_OUT_TEXT_DEFAULT
        : carmiData.logOutText;

    const loggedInTextState = compData.loggedInTextState
      ? compData.loggedInTextState
      : LoggedInTextState.MemberName;

    return {
      ...carmiData,
      ...compPropsRest,
      arrowShape: compProps.arrowShape ?? ArrowShape.Line,
      showBellIcon: compProps.showBellIcon ?? true,
      menuItemsAlignment: compProps.dropDownTextAlignment,
      translations: {
        loggedOutText,
        logOutText,
        menuOptionValueText:
          translate(LOGIN_SOCIAL_BAR_NAMESPACE, OPTION_VALUE_TEXT) ||
          CHOOSE_OPTION_TEXT_DEFAULT,
      },
      displayMode: loggedInDisplayMode,
      loggedInAvatarIcon,
      loggedInCustomAvatar: compData.loggedInCustomAvatar ?? '',
      loggedOutDisplayMode,
      loggedOutAvatarIcon,
      loggedOutCustomAvatar: compData.loggedOutCustomAvatar ?? '',
      showLoggedInText: compData.showLoggedInText ?? false,
      loggedInTextState,
      loggedInText: compData.loggedInText,
      customLoggedInText: compData.customLoggedInText ?? '',
      enableLoggingInStateInSSR,
    };
  },
);

export const css = withCompInfo<
  LoginSocialBarCSSVars,
  LoginSocialBarDefinition,
  LoginSocialBarCSSVars
>()(
  ['compProps', 'styleProperties', 'experiments'],
  ({ compProps, styleProperties }, carmiCss) => {
    let avatarAndArrowStrokeWidth: string;

    const typeofAvatarStrokeWidth =
      typeof styleProperties.avatarAndArrowStrokeWidth;
    // we receive avatarAndArrowStrokeWidth sometimes with 'px' sometimes without
    if (
      typeofAvatarStrokeWidth === 'number' ||
      (typeofAvatarStrokeWidth === 'string' &&
        !styleProperties.avatarAndArrowStrokeWidth.includes('px'))
    ) {
      avatarAndArrowStrokeWidth = `${styleProperties.avatarAndArrowStrokeWidth}px`;
    } else {
      avatarAndArrowStrokeWidth = styleProperties.avatarAndArrowStrokeWidth;
    }

    const loggedOutIconSize = compProps.loggedOutIconSize
      ? `${compProps.loggedOutIconSize}px`
      : carmiCss['--icon-size'];

    return {
      ...carmiCss,
      ...(compProps.arrowSize && {
        '--arrow-size': `${compProps.arrowSize}px`,
      }),
      ...(avatarAndArrowStrokeWidth && {
        '--avatarAndArrowStrokeWidth': avatarAndArrowStrokeWidth,
      }),
      '--logged-out-icon-size': loggedOutIconSize,
    };
  },
);

export const sdkData = withCompInfo<
  LoginSocialBarSdkData,
  LoginSocialBarDefinition,
  LoginSocialBarSdkData
>()([], (_deps, carmiSdkData) => {
  return carmiSdkData;
});

export const stateRefs = withStateRefsValues([
  'memberDetails',
  'reportBi',
  'currentUrl',
  'navigateTo',
  'logout',
  'promptLogin',
]);

export default createComponentMapperModel({ props, css, sdkData, stateRefs });
